import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Progress } from "@/components/ui/progress";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/context/AuthContext";
import { LimitReachedDialog } from "@/components/LimitReachedDialog";
import { useStorageUsage } from "@/hooks/useStorageUsage";
import { StorageUsageDetailsDialog } from "@/components/storage/StorageUsageDetailsDialog";
import { HardDrive } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

// Default storage limit of 10MB for free users
const DEFAULT_STORAGE_LIMIT = 10 * 1024 * 1024; // 10MB in bytes

interface SubscriptionData {
  plan_type: string;
  storage_limit: number;
  status: string;
  page_limit?: number;
}

const StorageUsage = ({ compact = false, variant }: { compact?: boolean; variant?: 'icon' }) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const { storageData, loading, formatSize } = useStorageUsage();
  const [subscription, setSubscription] = useState<SubscriptionData | null>(null);
  const [isChatEnabled, setIsChatEnabled] = useState<boolean | null>(null);
  const [showLimitDialog, setShowLimitDialog] = useState(false);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);

  const fetchSubscription = async () => {
    if (!user) return;
    
    const { data: subscriptionData } = await supabase
      .from('subscriptions')
      .select('plan_type, storage_limit, status, page_limit')
      .eq('user_id', user.id)
      .eq('status', 'active')
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();
      
    if (subscriptionData) {
      setSubscription(subscriptionData as SubscriptionData);
    } else {
      // Fallback for free users
      setSubscription({
        plan_type: 'free',
        storage_limit: DEFAULT_STORAGE_LIMIT,
        status: 'active',
        page_limit: 0
      });
    }
  };

  const fetchUserChatAccess = async () => {
    if (!user) return;
    
    try {
      const { data } = await supabase
        .from('users')
        .select('status_chat')
        .eq('id', user.id)
        .single();
      
      setIsChatEnabled(data?.status_chat);
    } catch (error) {
      console.error('Erro ao verificar acesso ao chat:', error);
      setIsChatEnabled(false);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      await Promise.all([
        fetchSubscription(),
        fetchUserChatAccess()
      ]);
    };

    loadData();
  }, [user]);

  useEffect(() => {
    if (!user) return;

    const timestamp = Date.now();

    const subscriptionChannel = supabase
      .channel(`storage-subscription-${user.id}-${timestamp}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'subscriptions',
          filter: `user_id=eq.${user.id}`
        },
        () => {
          console.log('StorageUsage: Subscription change detected, refetching...');
          fetchSubscription();
        }
      )
      .subscribe();

    const userChannel = supabase
      .channel(`storage-user-${user.id}-${timestamp}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'users',
          filter: `id=eq.${user.id}`
        },
        (payload) => {
          console.log('StorageUsage: User status change detected:', payload);
          if (payload.new && typeof payload.new === 'object' && 'status_chat' in payload.new) {
            setIsChatEnabled(payload.new.status_chat as boolean);
            
            toast({
              title: payload.new.status_chat ? "Chat Liberado" : "Chat Bloqueado",
              description: payload.new.status_chat 
                ? "Seu acesso ao chat foi restaurado."
                : "Seu acesso ao chat foi bloqueado. Entre em contato com o suporte.",
              variant: payload.new.status_chat ? "default" : "destructive",
            });
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(subscriptionChannel);
      supabase.removeChannel(userChannel);
    };
  }, [user, toast]);

  useEffect(() => {
    if (!user || !storageData || !subscription) return;
    
    const storagePercentage = storageData.percentage;
    
    if (subscription.plan_type !== 'premium' && storagePercentage >= 90) {
      setShowLimitDialog(true);
      
      const checkAndCreateStorageNotification = async () => {
        const { error } = await supabase.functions.invoke('check-storage-notification');
        
        if (error) {
          console.error('Erro ao verificar notificação de armazenamento:', error);
        }
      };
      
      checkAndCreateStorageNotification();
    }
  }, [storageData, subscription, user]);

  if (loading || !storageData) {
    if (variant === 'icon') {
      return <div className="h-9 w-9 rounded-full bg-muted animate-pulse" />;
    }
    return <Card className="p-3 space-y-2 animate-pulse">
      <div className="h-2 bg-gray-200 rounded"></div>
      <div className="h-4 bg-gray-200 rounded"></div>
    </Card>;
  }

  if (isChatEnabled === false) {
    return (
      <Card className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-amber-800">Chat Bloqueado</span>
          <Badge variant="destructive" className="text-xs">
            Bloqueado
          </Badge>
        </div>
        <p className="text-xs text-muted-foreground">
          Seu acesso ao chat foi bloqueado. Entre em contato com o suporte.
        </p>
      </Card>
    );
  }

  const { storageUsed, storageLimit, purchasedStorage, totalStorage, percentage, isAtLimit, isNearLimit } = storageData;

  // Icon variant for collapsed sidebar
  if (variant === 'icon') {
    const color = isAtLimit ? 'text-destructive' : isNearLimit ? 'text-yellow-500' : 'text-primary';
    return (
      <>
        <LimitReachedDialog
          open={showLimitDialog}
          onOpenChange={setShowLimitDialog}
          type="storage"
        />
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={() => setShowDetailsDialog(true)}
              className="relative flex items-center justify-center h-9 w-9 rounded-lg hover:bg-sidebar-accent transition-colors"
            >
              <HardDrive className={`h-5 w-5 ${color}`} />
              <span className={`absolute -bottom-0.5 -right-0.5 text-[8px] font-bold ${color} bg-sidebar rounded-full px-0.5 leading-none`}>
                {Math.round(percentage)}%
              </span>
            </button>
          </TooltipTrigger>
          <TooltipContent side="right" sideOffset={8}>
            <div className="text-xs">
              <div className="font-medium">Armazenamento</div>
              <div>{formatSize(storageUsed)} / {formatSize(totalStorage)}</div>
            </div>
          </TooltipContent>
        </Tooltip>
        <StorageUsageDetailsDialog
          open={showDetailsDialog}
          onOpenChange={setShowDetailsDialog}
        />
      </>
    );
  }

  if (compact) {
    return (
      <>
        <LimitReachedDialog
          open={showLimitDialog}
          onOpenChange={setShowLimitDialog}
          type="storage"
        />
        <Card 
          className="p-2 space-y-1 cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => setShowDetailsDialog(true)}
        >
          <Progress 
            value={percentage} 
            showPercentageInside={true}
            className={`h-4 ${isAtLimit ? 'bg-red-100' : isNearLimit ? 'bg-yellow-100' : 'bg-green-100'}`}
          />
          <span className="text-[10px] font-medium block">Armazenamento</span>
          <span className="text-[10px] text-muted-foreground block">
            {formatSize(storageUsed)} / {formatSize(totalStorage)}
          </span>
        </Card>
        <StorageUsageDetailsDialog
          open={showDetailsDialog}
          onOpenChange={setShowDetailsDialog}
        />
      </>
    );
  }

  return (
    <>
      <LimitReachedDialog
        open={showLimitDialog}
        onOpenChange={setShowLimitDialog}
        type="storage"
      />
      <Card 
        className="p-3 space-y-2 cursor-pointer hover:shadow-lg transition-shadow" 
        onClick={() => setShowDetailsDialog(true)}
      >
      <Progress 
        value={percentage} 
        showPercentageInside={true}
        className={`${isAtLimit ? 'bg-red-100' : isNearLimit ? 'bg-yellow-100' : 'bg-green-100'}`}
      />
      
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">Armazenamento</span>
        <span className="text-xs text-muted-foreground">
          {formatSize(storageUsed)} / {formatSize(totalStorage)}
        </span>
      </div>

      {purchasedStorage > 0 && (
        <div className="text-xs text-muted-foreground border-t pt-2">
          <div className="flex justify-between">
            <span>Plano:</span>
            <span>{formatSize(storageLimit)}</span>
          </div>
          <div className="flex justify-between text-purple-600 dark:text-purple-400">
            <span>Adicional:</span>
            <span>+{formatSize(purchasedStorage)}</span>
          </div>
        </div>
      )}

      {(isAtLimit || isNearLimit) && (
        <div className="flex justify-center">
          {isAtLimit && (
            <Badge variant="destructive" className="text-xs">
              Limite atingido
            </Badge>
          )}
          {isNearLimit && !isAtLimit && (
            <Badge variant="secondary" className="text-xs">
              Próximo do limite
            </Badge>
          )}
        </div>
      )}
      </Card>

      <StorageUsageDetailsDialog
        open={showDetailsDialog}
        onOpenChange={setShowDetailsDialog}
      />
    </>
  );
};

export default StorageUsage;
